import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import { 
  Globe, ShoppingBag, ArrowRight, Loader2, 
  FileText, Link2, Image, Check, User, LogIn, MessageSquare, Percent
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderFromEmbed() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState(null);
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [authData, setAuthData] = useState({ identifier: "", password: "", role: "advertiser" });
  const [authLoading, setAuthLoading] = useState(false);
  const [orderData, setOrderData] = useState({
    target_phrase: "",
    target_url: "",
    article_brief: "",
    requirements: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [hasIndividualPricing, setHasIndividualPricing] = useState(false);
  
  // Negocjacja
  const [showNegotiation, setShowNegotiation] = useState(false);
  const [negotiationOffer, setNegotiationOffer] = useState(null);
  const [negotiationData, setNegotiationData] = useState({
    proposed_price: "",
    message: ""
  });
  const [negotiationLoading, setNegotiationLoading] = useState(false);

  useEffect(() => {
    checkAuth();
  }, [siteId]);

  // Pobierz dane po zalogowaniu/wylogowaniu
  useEffect(() => {
    fetchData();
  }, [siteId, user]);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
      } catch {
        localStorage.removeItem("token");
        delete axios.defaults.headers.common["Authorization"];
      }
    }
  };

  const fetchData = async () => {
    try {
      // Pobierz dane strony
      const siteRes = await axios.get(`${API}/sites/${siteId}`);
      setSite(siteRes.data);
      
      // Sprawdz czy user jest zalogowany i jest reklamodawca
      // Jesli tak, pobierz ceny z rabatem
      if (user && user.role === "advertiser") {
        try {
          const pricingRes = await axios.get(`${API}/pricing-for-site/${siteId}`);
          if (pricingRes.data.pricing && pricingRes.data.pricing.length > 0) {
            setOffers(pricingRes.data.pricing.map((p, i) => ({
              id: `pricing-${i}`,
              name: p.name,
              price: p.final_price,
              base_price: p.base_price,
              discount_info: p.discount_info,
              description: p.description || p.duration || "",
              type: "article"
            })));
            setHasIndividualPricing(pricingRes.data.has_individual_pricing);
            return;
          }
        } catch (err) {
          console.log("No individual pricing, using standard");
        }
      }
      
      // Pobierz oferty publisher_offers
      const offersRes = await axios.get(`${API}/publisher/offers`);
      // Filtruj tylko oferty dla tej strony z cena > 0
      const siteOffers = offersRes.data.filter(o => {
        if (o.site_id !== siteId) return false;
        const price = o.publication_price || o.writing_price || o.link_price || 0;
        return price > 0;
      });
      
      // Jesli sa oferty publisher_offers, uzyj ich
      if (siteOffers.length > 0) {
        // Mapuj do ujednoliconego formatu
        const formattedOffers = siteOffers.map(o => ({
          id: o.id,
          name: o.description || "Publikacja artykulu",
          price: o.publication_price || o.writing_price || o.link_price,
          description: `Czas realizacji: ${o.turnaround_days || 3} dni`,
          type: o.link_price > 0 ? "link" : "article",
          original: o
        }));
        setOffers(formattedOffers);
        return;
      }
      
      // Pobierz cennik ze strony jesli brak ofert publisher_offers
      if (siteRes.data.pricing?.pricing) {
        // Filtruj tylko aktywne oferty z cena > 0
        const pricingOffers = siteRes.data.pricing.pricing
          .filter(p => {
            // Sprawdz czy oferta jest wlaczona (domyslnie true jesli nie ustawiono)
            const isEnabled = p.enabled !== false;
            // Sprawdz czy pokazywac w sklepie (domyslnie true jesli nie ustawiono)
            const showInShop = p.show_in_shop !== false;
            // Sprawdz czy cena > 0
            const hasPrice = (p.price || p.price_pln || 0) > 0;
            return isEnabled && showInShop && hasPrice;
          })
          .map((p, i) => ({
            id: `pricing-${i}`,
            name: p.name,
            price: p.price || p.price_pln || 0,
            description: p.description || p.duration || "",
            type: p.type || "article"
          }));
        setOffers(pricingOffers);
      }
    } catch (err) {
      toast.error("Nie udalo sie zaladowac danych strony");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const endpoint = authMode === "login" ? "/auth/login" : "/auth/register";
      const res = await axios.post(`${API}${endpoint}`, authData);
      localStorage.setItem("token", res.data.token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
      setUser(res.data.user);
      setShowAuth(false);
      toast.success(authMode === "login" ? "Zalogowano!" : "Konto utworzone!");
      // fetchData zostanie wywolane automatycznie przez useEffect
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad autoryzacji");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleNegotiation = async () => {
    if (!user) {
      toast.error("Musisz byc zalogowany aby negocjowac cene");
      setShowAuth(true);
      return;
    }
    
    if (!negotiationData.proposed_price || parseFloat(negotiationData.proposed_price) <= 0) {
      toast.error("Podaj proponowana cene");
      return;
    }
    
    setNegotiationLoading(true);
    try {
      await axios.post(`${API}/negotiations`, {
        site_id: siteId,
        offer_name: negotiationOffer.name,
        current_price: negotiationOffer.base_price || negotiationOffer.price,
        proposed_price: parseFloat(negotiationData.proposed_price),
        message: negotiationData.message
      });
      
      toast.success("Propozycja wyslana! Admin skontaktuje sie z Toba.");
      setShowNegotiation(false);
      setNegotiationOffer(null);
      setNegotiationData({ proposed_price: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad wysylania propozycji");
    } finally {
      setNegotiationLoading(false);
    }
  };

  const openNegotiation = (offer) => {
    if (!user) {
      toast.error("Zaloguj sie aby negocjowac cene");
      setShowAuth(true);
      return;
    }
    setNegotiationOffer(offer);
    setNegotiationData({
      proposed_price: "",
      message: ""
    });
    setShowNegotiation(true);
  };

  const handleOrder = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    
    if (!selectedOffer) {
      toast.error("Wybierz oferte");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        site_id: siteId,
        offer_name: selectedOffer.name,
        offer_type: selectedOffer.type || "article",
        price: selectedOffer.price || selectedOffer.publication_price || selectedOffer.writing_price,
        target_phrase: orderData.target_phrase,
        target_url: orderData.target_url,
        article_brief: orderData.article_brief,
        requirements: orderData.requirements,
        source: "embed_widget"
      };
      
      const res = await axios.post(`${API}/shop/order`, payload);
      toast.success("Zamowienie zlozone! Przekierowujemy do platnosci...");
      
      // Przekieruj do portfela lub strony zamowien
      setTimeout(() => {
        navigate("/zamowienia");
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad skladania zamowienia");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!site) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Strona nie znaleziona</h2>
            <p className="text-sm text-slate-500">Ta oferta nie jest juz dostepna.</p>
            <Button className="mt-4" onClick={() => navigate("/sklep")}>
              Przegladaj inne oferty
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <ShoppingBag className="w-4 h-4" />
            Zamow reklame
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
            {site.name}
          </h1>
          <p className="text-slate-500">
            <a href={site.url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
              {site.url}
            </a>
          </p>
          {site.category && (
            <Badge variant="secondary" className="mt-2">{site.category}</Badge>
          )}
        </div>

        {/* Auth Modal */}
        {showAuth && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-center">
                  {authMode === "login" ? "Zaloguj sie" : "Zaloz konto"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAuth} className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Email lub login</label>
                    <Input 
                      value={authData.identifier}
                      onChange={(e) => setAuthData({...authData, identifier: e.target.value})}
                      placeholder="twoj@email.pl"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Haslo</label>
                    <Input 
                      type="password"
                      value={authData.password}
                      onChange={(e) => setAuthData({...authData, password: e.target.value})}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={authLoading}>
                    {authLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : authMode === "login" ? (
                      <>
                        <LogIn className="w-4 h-4 mr-2" /> Zaloguj
                      </>
                    ) : (
                      <>
                        <User className="w-4 h-4 mr-2" /> Zaloz konto
                      </>
                    )}
                  </Button>
                  <p className="text-center text-sm text-slate-500">
                    {authMode === "login" ? "Nie masz konta?" : "Masz juz konto?"}
                    <button 
                      type="button"
                      className="text-blue-600 ml-1 hover:underline"
                      onClick={() => setAuthMode(authMode === "login" ? "register" : "login")}
                    >
                      {authMode === "login" ? "Zaloz konto" : "Zaloguj sie"}
                    </button>
                  </p>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => setShowAuth(false)}
                  >
                    Anuluj
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* User status */}
        {user && (
          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-800">
                Zalogowany jako <strong>{user.display_name || user.identifier}</strong>
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                localStorage.removeItem("token");
                setUser(null);
              }}
            >
              Wyloguj
            </Button>
          </div>
        )}

        {/* Offers selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-blue-600" />
              Wybierz usluge
              {hasIndividualPricing && (
                <Badge className="bg-green-100 text-green-700 ml-2">
                  <Percent className="w-3 h-3 mr-1" /> Twoje ceny
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {offers.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                Brak dostepnych ofert dla tej strony
              </p>
            ) : (
              offers.map((offer) => {
                const price = offer.price || offer.publication_price || offer.writing_price || 0;
                const isSelected = selectedOffer?.id === offer.id;
                
                return (
                  <div key={offer.id} className="relative">
                    <button
                      onClick={() => setSelectedOffer(offer)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        isSelected 
                          ? "border-blue-500 bg-blue-50" 
                          : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {offer.type === "link" ? (
                              <Link2 className="w-4 h-4 text-emerald-600" />
                            ) : offer.type === "banner" ? (
                              <Image className="w-4 h-4 text-purple-600" />
                            ) : (
                              <FileText className="w-4 h-4 text-blue-600" />
                            )}
                            <span className="font-semibold text-slate-800">
                              {offer.name || "Publikacja artykulu"}
                            </span>
                            {offer.discount_info && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                {offer.discount_info}
                              </Badge>
                            )}
                          </div>
                          {offer.description && (
                            <p className="text-xs text-slate-500 mt-1">{offer.description}</p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          {offer.base_price && offer.base_price !== price && (
                            <p className="text-sm text-slate-400 line-through">{offer.base_price} PLN</p>
                          )}
                          <p className="text-xl font-bold text-blue-600">{price} PLN</p>
                          {isSelected && (
                            <Badge className="mt-1 bg-blue-600">Wybrano</Badge>
                          )}
                        </div>
                      </div>
                    </button>
                    
                    {/* Przycisk negocjacji */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openNegotiation(offer);
                      }}
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                      title="Negocjuj cene"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Order details */}
        {selectedOffer && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Szczegoly zamowienia
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600">
                    Fraza kluczowa (anchor text)
                  </label>
                  <Input 
                    value={orderData.target_phrase}
                    onChange={(e) => setOrderData({...orderData, target_phrase: e.target.value})}
                    placeholder="np. najlepszy sklep online"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">
                    URL docelowy (link)
                  </label>
                  <Input 
                    value={orderData.target_url}
                    onChange={(e) => setOrderData({...orderData, target_url: e.target.value})}
                    placeholder="https://twoja-strona.pl"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-slate-600">
                  Temat artykulu / brief
                </label>
                <Textarea 
                  value={orderData.article_brief}
                  onChange={(e) => setOrderData({...orderData, article_brief: e.target.value})}
                  placeholder="Opisz o czym ma byc artykul..."
                  rows={3}
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-slate-600">
                  Dodatkowe wymagania (opcjonalnie)
                </label>
                <Textarea 
                  value={orderData.requirements}
                  onChange={(e) => setOrderData({...orderData, requirements: e.target.value})}
                  placeholder="Inne uwagi dla autora..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary and submit */}
        {selectedOffer && (
          <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Podsumowanie</p>
                  <p className="text-xl font-bold">{selectedOffer.name || "Publikacja artykulu"}</p>
                  <p className="text-blue-200 text-sm">{site.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-blue-100 text-sm">Do zaplaty</p>
                  <p className="text-3xl font-bold">
                    {selectedOffer.price || selectedOffer.publication_price || selectedOffer.writing_price} PLN
                  </p>
                </div>
              </div>
              
              <Button 
                className="w-full mt-6 h-12 bg-white text-blue-600 hover:bg-blue-50 font-bold text-lg"
                onClick={handleOrder}
                disabled={submitting}
                data-testid="submit-order-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : user ? (
                  <>
                    Zloz zamowienie <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  <>
                    Zaloguj sie i zamow <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              {!user && (
                <p className="text-center text-blue-200 text-xs mt-3">
                  Musisz sie zalogowac lub zarejestrowac aby zlozyc zamowienie
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-slate-500 pt-4">
          <p>Powered by <a href="https://epidemos.pl" className="text-blue-600 hover:underline">Epidemos.pl</a></p>
          <p className="text-xs mt-1">Platforma content marketingu i publikacji artykulow</p>
        </div>
      </div>

      {/* Dialog negocjacji */}
      <Dialog open={showNegotiation} onOpenChange={setShowNegotiation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-600" />
              Negocjuj cene
            </DialogTitle>
          </DialogHeader>
          
          {negotiationOffer && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">Oferta:</p>
                <p className="font-medium">{negotiationOffer.name}</p>
                <p className="text-sm text-slate-500">Strona: {site?.name}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm text-slate-600">Aktualna cena:</span>
                  <span className="font-bold text-blue-600">
                    {negotiationOffer.base_price || negotiationOffer.price} PLN
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Proponowana cena (PLN)</label>
                <Input
                  type="number"
                  value={negotiationData.proposed_price}
                  onChange={(e) => setNegotiationData({...negotiationData, proposed_price: e.target.value})}
                  placeholder="np. 80"
                  min="1"
                  step="1"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Wiadomosc (opcjonalnie)</label>
                <Textarea
                  value={negotiationData.message}
                  onChange={(e) => setNegotiationData({...negotiationData, message: e.target.value})}
                  placeholder="Np. Jestem stalym klientem, zamawiam regularnie..."
                  rows={3}
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowNegotiation(false)}
                >
                  Anuluj
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleNegotiation}
                  disabled={negotiationLoading}
                >
                  {negotiationLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Wyslij propozycje"
                  )}
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                Twoja propozycja zostanie wyslana do administratora i wydawcy strony.
                Otrzymasz odpowiedz w wiadomosciach systemowych.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
